/*
 * ew-chassis-picker.js — CHASSIS-NAV-1 Phase 2
 *
 * ONE unified chassis-picker component. Two levels:
 *   L1 — SERIES LEDGER: 15 fixed-order series rows, each a mono overline
 *        label + a bordered row of chassis CHIPS (code + year span + M badge).
 *   L2 — INLINE EXPANSION (ARIA accordion/disclosure, NO navigation): clicking
 *        a chip expands a panel beneath that chip's series row with a
 *        silhouette (or typographic-header fallback) + the model list. A
 *        model (or engine-distinct trim) click resolves engine_family behind
 *        the scenes and routes EXACTLY as configure-ymmt does today.
 *
 * AUTO-SKIP: a single-model chassis resolves straight from the chip click.
 *
 * Data source: assets/ew-chassis-data.json, fetched via data-asset-url —
 * same fetch idiom as ew-homepage-vehicle-input.js. Normalized rows:
 *   code, make, model, trim, engine, engine_family, series, m_car, ys, ye
 *
 * The picker NEVER writes data; it only writes the active vehicle through the
 * same funnels configure-ymmt uses (EWVehicleResolver.write + ewConfigurator.set)
 * and navigates to /pages/configure-stage?chassis=CODE.
 *
 * Fast lanes (typeahead + Garage-remembered) sit ABOVE this component and are
 * untouched — this module owns only [data-ew-chassis-picker] roots.
 */
(function () {
  'use strict';

  /* CHASSIS-NAV-1 Phase 3b — this module now loads in the global header
     (alongside the singleton Garage dialog) AND in the chassis-picker /
     shop-by-vehicle sections, so the same <script src> can appear twice on one
     page. Guard so the IIFE body runs once; init() is independently idempotent
     (per-root _ewCpBound), but skipping the re-run avoids redundant work. */
  if (window.__ewChassisPickerLoaded) return;
  window.__ewChassisPickerLoaded = true;

  /* LP-locked series order. Drives row order; we DO NOT re-derive grouping —
     the `series` field is authoritative for membership. */
  var SERIES_ORDER = [
    '1 Series', '2 Series', '3 Series', '4 Series', '5 Series',
    '6 Series', '7 Series', '8 Series',
    'X1/X2', 'X3/X4', 'X5/X6', 'X7', 'Z4', 'MINI', 'Supra'
  ];

  /* R2 2.1 — one implementation, in assets/ew-engine-family.js (loaded
     un-deferred above every consumer). The dataset already carries
     engine_family; this normalises rows that might lack it. */
  var engineFamily = window.EWEngineFamily;

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c];
    });
  }
  function yr2(y) { var s = String(y || ''); return s.length === 4 ? s.slice(2) : s; }

  /* ---- ///M chip badge — ONE shared, static markup string ----
     Decorative tri-colour slashes (aria-hidden; meaning carried by the chip
     aria-label) + a visible white "M". Three separate slash spans so CSS can
     colour each independently. Pure CSS/type — no SVG, no m_logo. */
  function mBadgeHtml() {
    return '<span class="ew-cp-chip__m">' +
      '<span class="ew-cp-chip__m-slashes" aria-hidden="true">' +
        '<span>/</span><span>/</span><span>/</span>' +
      '</span>' +
      '<span class="ew-cp-chip__m-letter">M</span>' +
      '</span>';
  }

  /* ---- Inferred BODY-STYLE tag for a chip ----
     Lets a non-expert tell same-series chassis apart (F32 Coupe vs F33
     Convertible vs F36 Gran Coupe). We derive, never store: read the chassis
     model rows for an explicit body word (longest-first so 'Gran Coupe' wins
     over 'Coupe'); else fall back to a per-series default. X-series SUVs are
     already split per X-model marker, so they get NO body tag (return ''). */
  var BODY_KEYWORDS = ['Gran Coupe', 'Gran Turismo', 'Gran Tourer', 'Active Tourer', 'Hardtop 4-door', 'Hardtop 2-door', 'Convertible', 'Roadster', 'Touring', 'Coupe', 'Sedan', 'Clubman', 'Countryman', 'GT'];
  var BODY_DEFAULT = { '1 Series': 'Hatchback', '2 Series': 'Coupe', '3 Series': 'Sedan', '4 Series': 'Coupe', '5 Series': 'Sedan', '7 Series': 'Sedan', 'Z4': 'Roadster', 'Supra': 'Coupe' };
  var X_SERIES = { 'X1/X2': 1, 'X3/X4': 1, 'X5/X6': 1, 'X7': 1 };
  function bodyOf(chassis) {
    if (X_SERIES[chassis.series]) return '';                // SUVs already split per X-model marker — no body tag
    var models = (chassis.rows || []).map(function (r) { return String(r.model || ''); });
    for (var i = 0; i < BODY_KEYWORDS.length; i++) {        // explicit body word wins (longest first)
      var kw = BODY_KEYWORDS[i].toLowerCase();
      for (var j = 0; j < models.length; j++) {
        if (models[j].toLowerCase().indexOf(kw) >= 0) return BODY_KEYWORDS[i];
      }
    }
    return BODY_DEFAULT[chassis.series] || '';              // else the series default
  }

  /* ---- Build the chassis index from the flat dataset ---- */
  function buildIndex(rows) {
    /* code -> { code, series, make, m_car, ys, ye, rows:[...] } */
    var byCode = {};
    rows.forEach(function (r) {
      if (!r || !r.code) return;
      var c = byCode[r.code];
      if (!c) {
        c = byCode[r.code] = {
          code: r.code, series: r.series, make: r.make,
          m_car: false, ys: null, ye: null, rows: []
        };
      }
      c.rows.push(r);
      if (r.m_car) c.m_car = true;
      var ys = parseInt(r.ys, 10), ye = parseInt(r.ye, 10);
      if (ys && (c.ys == null || ys < c.ys)) c.ys = ys;
      if (ye && (c.ye == null || ye > c.ye)) c.ye = ye;
      /* Series safety: a chassis should be single-series, but if mixed, the
         first non-empty wins (matches "do not re-derive" — trust the field). */
      if (!c.series && r.series) c.series = r.series;
    });

    /* series -> [chassis...] in a stable order (M cars last, then by code) */
    var bySeries = {};
    Object.keys(byCode).forEach(function (code) {
      var c = byCode[code];
      var s = c.series || 'Other';
      (bySeries[s] = bySeries[s] || []).push(c);
    });
    Object.keys(bySeries).forEach(function (s) {
      bySeries[s].sort(function (a, b) {
        /* non-M before M, then numeric-aware by code */
        if (a.m_car !== b.m_car) return a.m_car ? 1 : -1;
        return String(a.code).localeCompare(String(b.code), undefined, { numeric: true });
      });
    });
    return { byCode: byCode, bySeries: bySeries };
  }

  /* ---- Group a chassis's rows into the model list (L2) ----
     Group by model. Trims collapse INTO the model row, UNLESS a trim changes
     the engine — then that trim surfaces as its own row. Each emitted row
     carries a representative dataset row for resolution. */
  function modelRows(chassis) {
    var byModel = {};
    var order = [];
    chassis.rows.forEach(function (r) {
      if (!byModel[r.model]) { byModel[r.model] = []; order.push(r.model); }
      byModel[r.model].push(r);
    });
    var out = [];
    order.forEach(function (model) {
      var rs = byModel[model];
      /* distinct engine families within this model */
      var fams = {};
      rs.forEach(function (r) { fams[engineFamily(r.engine_family || r.engine)] = true; });
      var famKeys = Object.keys(fams);
      if (famKeys.length <= 1) {
        /* single engine — one model row, trims collapse in */
        out.push(makeModelEntry(model, rs, null));
      } else {
        /* engine-distinct trims surface as their own rows */
        var byFam = {};
        var famOrder = [];
        rs.forEach(function (r) {
          var f = engineFamily(r.engine_family || r.engine);
          if (!byFam[f]) { byFam[f] = []; famOrder.push(f); }
          byFam[f].push(r);
        });
        famOrder.forEach(function (f) {
          out.push(makeModelEntry(model, byFam[f], f));
        });
      }
    });
    return out;
  }

  function makeModelEntry(model, rs, famSuffix) {
    var ys = null, ye = null, mCar = false;
    rs.forEach(function (r) {
      var a = parseInt(r.ys, 10), b = parseInt(r.ye, 10);
      if (a && (ys == null || a < ys)) ys = a;
      if (b && (ye == null || b > ye)) ye = b;
      if (r.m_car) mCar = true;
    });
    var rep = rs[0];
    var fam = engineFamily(rep.engine_family || rep.engine);
    /* When we split by engine, append the engine family to disambiguate the
       label (e.g. "X5 M60i · N63" vs a B58 variant of the same model). */
    var label = model + (famSuffix ? ' · ' + famSuffix : '');
    return {
      label: label,
      model: model,
      ys: ys, ye: ye,
      m_car: mCar,
      engine_family: fam,
      engine_code: rep.engine,
      make: rep.make,
      trim: rep.trim || ''
    };
  }

  /* ---- Resolution — engine_family resolution is IDENTICAL in every mode ----
     Builds the canonical vehicle from the chosen chassis + model entry. The
     post-resolve ACTION (route vs filter) is the only thing that differs, and
     is owned by resolveAndAct() below, keyed off the picker's onselect mode. */
  function buildVehicle(chassis, entry) {
    /* F-05 — the picker never knows the visitor's exact model year, so it
       never writes one: the honest ys/ye BAND travels on the vehicle and
       the label renders the band form via the canonical shared formatter
       (the band end is never styled as a model year). */
    var v = {
      code: chassis.code,
      make: entry.make || chassis.make || 'BMW',
      model: entry.model || '',
      trim: '',
      engine_code: entry.engine_code || '',
      ys: entry.ys || chassis.ys || '',
      ye: entry.ye || chassis.ye || '',
      entry_mode: 'chassis-picker',
      source: 'chassis-picker'
    };
    var R = window.EWVehicleResolver;
    v.label = (R && typeof R.displayLabel === 'function')
      ? (R.displayLabel(v) || chassis.code)
      : ((v.ys && v.ye)
          ? [v.ys + '–' + v.ye, chassis.code, v.model].filter(Boolean).join(' ')
          : ([v.make, v.model].filter(Boolean).join(' ') || chassis.code));
    return v;
  }

  /* Persist the canonical vehicle through the SAME funnel configure-ymmt /
     typeahead use. EWVehicleResolver.write also dispatches ew:vehicle-resolved,
     which is the signal the filter-mode host (shop-by-vehicle) reacts to. */
  function writeActive(vehicle, setConfigurator) {
    if (window.EWVehicleResolver && typeof window.EWVehicleResolver.write === 'function') {
      window.EWVehicleResolver.write(vehicle);
    }
    if (setConfigurator && window.ewConfigurator && typeof window.ewConfigurator.set === 'function') {
      window.ewConfigurator.set({ vehicle: vehicle, meta: { current_step: 'stage' } });
    }
  }

  /* ---- Post-resolve action, keyed off the picker's on-select mode ----
       'route'  (default): writeActive + ewConfigurator.set, then navigate to
                 /pages/configure-stage?chassis=CODE&engine=FAMILY — EXACTLY
                 today's configure-ymmt behavior (unchanged).
       'filter': writeActive ONLY (which fires ew:vehicle-resolved); the host
                 page reacts to that signal and filters in place. NO navigation
                 and NO ewConfigurator state mutation.
       'emit'   (CHASSIS-NAV-1 Phase 3b — Garage dialog host): resolve the
                 vehicle (engine_family IDENTICAL to the other modes) but do
                 NOT persist, do NOT navigate, and do NOT fire the global
                 ew:vehicle-resolved (which would commit the pick + repaint the
                 pill before the dialog's own Save/route step). Instead dispatch
                 a host-scoped ew:cp-emit on the picker root carrying the chassis
                 + entry + resolved vehicle, so the host can STAGE it into its
                 own stage→Save→per-context-route flow. The root is included on
                 the event so a host can scope to its own picker instance. */
  function resolveAndAct(mode, chassis, entry, root) {
    var vehicle = buildVehicle(chassis, entry);

    if (mode === 'emit') {
      var fam = engineFamily(entry.engine_family || entry.engine_code);
      var detail = { chassis: chassis, entry: entry, vehicle: vehicle, engine_family: fam };
      var target = root || document;
      target.dispatchEvent(new CustomEvent('ew:cp-emit', { detail: detail, bubbles: true }));
      return;
    }

    if (mode === 'filter') {
      writeActive(vehicle, false);
      return;
    }

    /* default: route */
    writeActive(vehicle, true);
    var url = '/pages/configure-stage?chassis=' + encodeURIComponent(chassis.code);
    var fam = engineFamily(entry.engine_family || entry.engine_code);
    if (fam) url += '&engine=' + encodeURIComponent(fam);
    window.location.href = url;
  }

  /* ============================================================
     Component instance
     ============================================================ */
  function Picker(root) {
    this.root = root;
    this.compact = root.getAttribute('data-compact') === 'true';
    /* On-select mode: 'route' (default — configure-ymmt), 'filter'
       (shop-by-vehicle: write active vehicle + signal, no navigation), or
       'emit' (Garage dialog: dispatch ew:cp-emit for the host to stage,
       no write/nav/global-signal). */
    var mode = root.getAttribute('data-ew-cp-onselect');
    this.onselect = (mode === 'filter' || mode === 'emit') ? mode : 'route';
    this.ledger = root.querySelector('[data-ew-cp-ledger]');
    this.live = root.querySelector('[data-ew-cp-live]');
    this.silIsland = root.querySelector('[data-ew-cp-silhouettes]');
    this.openCode = null;         /* currently expanded chassis code */
    this.lastChipBtn = null;      /* chip to return focus to on collapse */
    this.index = null;
    this.silByCode = this.readSilhouettes();
  }

  Picker.prototype.readSilhouettes = function () {
    /* Optional server-rendered island: a JSON map of { CODE: "<svg…>" }.
       Every silhouette_svg is currently NULL, so this is purely additive. */
    if (!this.silIsland) return {};
    try {
      var map = JSON.parse(this.silIsland.textContent.trim() || '{}');
      return (map && typeof map === 'object') ? map : {};
    } catch (_) { return {}; }
  };

  Picker.prototype.announce = function (msg) {
    if (this.live) this.live.textContent = msg;
  };

  Picker.prototype.load = function () {
    var self = this;
    var url = this.root.getAttribute('data-asset-url');
    if (!url) { this.renderError(); return; }
    fetch(url, { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (json) {
        var rows = Array.isArray(json) ? json : [];
        self.index = buildIndex(rows);
        self.renderLedger();
      })
      .catch(function () { self.renderError(); });
  };

  Picker.prototype.renderError = function () {
    if (this.ledger) {
      this.ledger.innerHTML = '<p class="ew-cp__error">Couldn’t load the chassis list. ' +
        'Use the search above, or <a href="/pages/contact">tell us about your car</a>.</p>';
    }
  };

  Picker.prototype.chipHtml = function (chassis) {
    var span = (chassis.ys && chassis.ye)
      ? esc(chassis.ys) + '–' + esc(yr2(chassis.ye))
      : '';
    var body = bodyOf(chassis);
    var mBadge = chassis.m_car ? mBadgeHtml() : '';
    var aria = chassis.code + (span ? ', ' + chassis.ys + ' to ' + chassis.ye : '') +
      (body ? ', ' + body : '') +
      (chassis.m_car ? ', M model' : '');
    return '<button type="button" class="ew-cp-chip" ' +
      'data-ew-cp-chip="' + esc(chassis.code) + '" ' +
      'aria-expanded="false" ' +
      'aria-label="' + esc(aria) + '">' +
      '<span class="ew-cp-chip__code">' + esc(chassis.code) + '</span>' +
      (span ? '<span class="ew-cp-chip__yrs">' + span + '</span>' : '') +
      (body ? '<span class="ew-cp-chip__body">' + esc(body) + '</span>' : '') +
      mBadge +
      '</button>';
  };

  /* Split a series into the sub-rows we render. ONLY the paired X-series get
     split into per-model rows (X1 vs X2, etc.); every other series stays one
     row. We partition WITHOUT re-sorting — the per-row order set by buildIndex
     is preserved inside each sub-list. The model→family key reads the
     representative dataset row carried on each chassis index entry (c.rows[0],
     produced by buildIndex). */
  function subRowsFor(series, list) {
    if (series === 'X1/X2' || series === 'X3/X4' || series === 'X5/X6') {
      var groups = {}, order = [];
      list.forEach(function (c) {
        var rep = (c.rows && c.rows[0]) || {};
        var m = String(rep.model || '').match(/^X\d/);   /* 'X3 30i' -> 'X3' */
        var key = m ? m[0] : series;
        if (!groups[key]) { groups[key] = []; order.push(key); }
        groups[key].push(c);
      });
      order.sort();   /* X1 before X2, etc. (string sort is correct for X1..X9) */
      return order.map(function (k) { return { header: k, list: groups[k] }; });
    }
    return [{ header: series, list: list }];
  }

  Picker.prototype.renderLedger = function () {
    if (!this.index) return;
    var self = this;
    var html = '';
    SERIES_ORDER.forEach(function (series) {
      var list = self.index.bySeries[series];
      if (!list || !list.length) return; /* don't render empty series rows */
      html +=
        '<div class="ew-cp-band" data-ew-cp-series="' + esc(series) + '">' +
          subRowsFor(series, list).map(function (sr) {
            return '<div class="ew-cp-row" role="group" aria-label="' + esc(sr.header) + '" data-ew-cp-series="' + esc(series) + '">' +
              '<span class="ew-cp-row__header-tile">' + esc(sr.header) + '</span>' +
              '<div class="ew-cp-row__chips">' +
                sr.list.map(function (c) { return self.chipHtml(c); }).join('') +
              '</div>' +
              '<div class="ew-cp-panel" data-ew-cp-panel hidden></div>' +
            '</div>';
          }).join('') +
        '</div>';
    });
    this.ledger.innerHTML = html;
  };

  Picker.prototype.panelHtml = function (chassis) {
    var rows = modelRows(chassis);
    var yrs = (chassis.ys && chassis.ye) ? chassis.ys + '–' + chassis.ye : '';
    var rep = chassis.rows[0] || {};
    var name = rep.make ? (rep.make) : '';

    /* Silhouette (additive) or typographic header fallback. */
    var sil = this.silByCode[chassis.code];
    var headArt;
    if (sil) {
      headArt = '<div class="ew-cp-panel__sil" aria-hidden="true">' + sil + '</div>';
    } else {
      headArt = '<div class="ew-cp-panel__typo" aria-hidden="true">' +
        '<span class="ew-cp-panel__typo-code">' + esc(chassis.code) + '</span>' +
        (yrs ? '<span class="ew-cp-panel__typo-yrs">' + esc(yrs) + '</span>' : '') +
        '</div>';
    }

    var heading = chassis.code + ' · ' + esc(chassis.series || '') +
      (yrs ? ' · ' + yrs : '');

    var listHtml = rows.map(function (m) {
      var mYrs = (m.ys && m.ye) ? m.ys + '–' + m.ye : '';
      var mBadge = m.m_car ? '<span class="ew-cp-model__m" aria-hidden="true">M</span>' : '';
      return '<button type="button" class="ew-cp-model" ' +
        'data-ew-cp-model="' + esc(m.model) + '" ' +
        'data-ew-cp-engine="' + esc(m.engine_family) + '" ' +
        'data-ew-cp-engine-code="' + esc(m.engine_code) + '">' +
        '<span class="ew-cp-model__name">' + esc(m.label) + mBadge + '</span>' +
        (mYrs ? '<span class="ew-cp-model__yrs">' + esc(mYrs) + '</span>' : '') +
        '<span class="ew-cp-model__go" aria-hidden="true">→</span>' +
        '</button>';
    }).join('');

    return '<div class="ew-cp-panel__inner">' +
        '<div class="ew-cp-panel__head">' +
          headArt +
          '<div class="ew-cp-panel__meta">' +
            '<div class="ew-overline ew-overline--accent">SELECT YOUR MODEL</div>' +
            '<h3 class="ew-cp-panel__title">' + heading + '</h3>' +
          '</div>' +
          '<button type="button" class="ew-cp-panel__close" data-ew-cp-close ' +
            'aria-label="Close ' + esc(chassis.code) + ' models">×</button>' +
        '</div>' +
        '<div class="ew-cp-panel__models" role="group" ' +
          'aria-label="' + esc(chassis.code) + ' models">' +
          listHtml +
        '</div>' +
        '<button type="button" class="ew-cp-panel__back ew-link-mono" data-ew-cp-close>' +
          '← Back to all chassis' +
        '</button>' +
      '</div>';
  };

  Picker.prototype.collapse = function (returnFocus) {
    if (!this.openCode) return;
    var chipBtn = this.lastChipBtn;
    var row = this.root.querySelector('[data-ew-cp-chip="' + cssEsc(this.openCode) + '"]');
    row = row && row.closest('.ew-cp-row');
    var panel = row && row.querySelector('[data-ew-cp-panel]');
    if (chipBtn) chipBtn.setAttribute('aria-expanded', 'false');
    if (panel) { panel.hidden = true; panel.innerHTML = ''; }
    if (row) row.classList.remove('is-open');
    this.openCode = null;
    if (returnFocus && chipBtn) chipBtn.focus();
    this.lastChipBtn = null;
  };

  Picker.prototype.expand = function (chassis, chipBtn) {
    /* close any other open panel first (one at a time) */
    if (this.openCode && this.openCode !== chassis.code) this.collapse(false);

    var row = chipBtn.closest('.ew-cp-row');
    var panel = row && row.querySelector('[data-ew-cp-panel]');
    if (!panel) return;

    panel.innerHTML = this.panelHtml(chassis);
    panel.hidden = false;
    row.classList.add('is-open');
    chipBtn.setAttribute('aria-expanded', 'true');
    this.openCode = chassis.code;
    this.lastChipBtn = chipBtn;

    this.announce(chassis.code + ' selected. Choose a model.');

    /* Move focus into the panel (first model button), per disclosure a11y. */
    var firstModel = panel.querySelector('[data-ew-cp-model]');
    if (firstModel) firstModel.focus();
  };

  Picker.prototype.onChipActivate = function (chipBtn) {
    var code = chipBtn.getAttribute('data-ew-cp-chip');
    var chassis = this.index && this.index.byCode[code];
    if (!chassis) return;

    /* AUTO-SKIP: single-model chassis resolves straight from the chip. */
    var rows = modelRows(chassis);
    if (rows.length === 1) {
      resolveAndAct(this.onselect, chassis, rows[0], this.root);
      return;
    }

    /* toggle */
    if (this.openCode === code) { this.collapse(true); return; }
    this.expand(chassis, chipBtn);
  };

  Picker.prototype.bind = function () {
    var self = this;

    this.root.addEventListener('click', function (e) {
      var chip = e.target.closest('[data-ew-cp-chip]');
      if (chip && self.root.contains(chip)) { self.onChipActivate(chip); return; }

      var model = e.target.closest('[data-ew-cp-model]');
      if (model && self.root.contains(model)) {
        var code = self.openCode;
        var chassis = self.index && self.index.byCode[code];
        if (!chassis) return;
        var entry = {
          model: model.getAttribute('data-ew-cp-model'),
          engine_family: model.getAttribute('data-ew-cp-engine'),
          engine_code: model.getAttribute('data-ew-cp-engine-code'),
          make: chassis.make,
          ys: chassis.ys, ye: chassis.ye
        };
        resolveAndAct(self.onselect, chassis, entry, self.root);
        return;
      }

      var close = e.target.closest('[data-ew-cp-close]');
      if (close && self.root.contains(close)) { self.collapse(true); return; }
    });

    /* Esc closes the open panel and returns focus to its chip. */
    this.root.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && self.openCode) {
        e.stopPropagation();
        self.collapse(true);
      }
    });
  };

  /* CSS.escape fallback for attribute selectors (codes are [A-Z0-9], safe). */
  function cssEsc(s) {
    if (window.CSS && CSS.escape) return CSS.escape(s);
    return String(s).replace(/["\\\]]/g, '\\$&');
  }

  function init() {
    var roots = Array.prototype.slice.call(document.querySelectorAll('[data-ew-chassis-picker]'));
    roots.forEach(function (root) {
      if (root._ewCpBound) return;
      root._ewCpBound = true;
      var p = new Picker(root);
      p.bind();
      p.load();
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
